import { buildMediaUrl } from '../../utils/buildMediaUrl'

function isAbortLikeError(error) {
  return error?.name === 'AbortError' || error?.code === 20
}

function shouldIgnorePlayError(error) {
  return isAbortLikeError(error) || error?.name === 'NotAllowedError'
}

function getPwaDebugContext() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return {}
  }

  const nav = typeof navigator !== 'undefined' ? navigator : {}
  const standaloneDisplayMode = Boolean(
    window.matchMedia?.('(display-mode: standalone)').matches,
  )
  const fullscreenDisplayMode = Boolean(
    window.matchMedia?.('(display-mode: fullscreen)').matches,
  )
  const navigatorStandalone = Boolean(nav.standalone)
  const userAgent = nav.userAgent || ''
  const platform = nav.platform || ''
  const isIOS =
    /iPad|iPhone|iPod/.test(userAgent) ||
    (platform === 'MacIntel' && nav.maxTouchPoints > 1)

  return {
    visibilityState: document.visibilityState,
    hidden: document.hidden,
    isPWA:
      standaloneDisplayMode ||
      fullscreenDisplayMode ||
      navigatorStandalone ||
      document.referrer.includes('android-app://'),
    standaloneDisplayMode,
    fullscreenDisplayMode,
    navigatorStandalone,
    isIOS,
    userAgent,
  }
}

function getAudioDebugContext(audio) {
  if (!audio) return { hasAudio: false }

  return {
    hasAudio: true,
    paused: audio.paused,
    ended: audio.ended,
    readyState: audio.readyState,
    networkState: audio.networkState,
    currentTime: audio.currentTime,
    duration: audio.duration,
    src: audio.currentSrc || audio.src,
  }
}

function formatPlaybackError(error) {
  if (!error) return undefined

  return {
    name: error.name,
    message: error.message,
    code: error.code,
  }
}

function logControllerDebug(event, audio, context = {}) {
  console.log('[PlaybackDebug]', event, {
    ...getPwaDebugContext(),
    audio: getAudioDebugContext(audio),
    ...context,
  })
}

export function logPlayError(error, context = {}) {
  if (shouldIgnorePlayError(error)) {
    console.warn('Audio play() was rejected', {
      name: error?.name,
      message: error?.message,
      ...context,
    })
    return
  }

  console.error('Playback error:', error, context)
}

export function waitForAudioReady(audio, generation, generationRef) {
  return new Promise((resolve, reject) => {
    if (!audio) {
      resolve()
      return
    }

    const isStale = () =>
      generationRef &&
      generation != null &&
      generationRef.current !== generation

    const cleanup = (onCanPlay, onError) => {
      audio.removeEventListener('canplay', onCanPlay)
      audio.removeEventListener('error', onError)
    }

    if (audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      resolve()
      return
    }

    const onCanPlay = () => {
      cleanup(onCanPlay, onError)
      resolve()
    }

    const onError = () => {
      cleanup(onCanPlay, onError)
      if (isStale()) resolve()
      else reject(new Error('Audio element error'))
    }

    audio.addEventListener('canplay', onCanPlay, { once: true })
    audio.addEventListener('error', onError, { once: true })
  })
}

export async function requestAudioPlay(audio, context = {}) {
  if (!audio) return false

  try {
    // logControllerDebug('audio-play-request', audio, context)
    const playResult = audio.play()
    if (playResult !== undefined) {
      await playResult
    }
    // logControllerDebug('audio-play-resolved', audio, context)
    return true
  } catch (error) {
    logControllerDebug('audio-play-rejected', audio, {
      ...context,
      error: formatPlaybackError(error),
    })
    logPlayError(error, context)
    return false
  }
}

export async function loadTrackIntoAudio({
  audio,
  track,
  API_URL,
  generation,
  generationRef,
  autoplay = false,
  reason = 'unknown',
}) {
  if (!audio || !track) {
    return { ok: false, skipped: true }
  }

  const nextSrc = buildMediaUrl(track, API_URL, 'audio')
  if (!nextSrc) {
    throw new Error('Unable to resolve audio source for track')
  }

  audio.preload = 'auto'

  if (audio.src !== nextSrc) {
    audio.src = nextSrc
  }

  // logControllerDebug('audio-load-before', audio, {
  //   autoplay,
  //   generation,
  //   reason,
  //   src: nextSrc,
  //   trackKey: `${track.type}|${track.creator}|${track.title}`,
  // })
  audio.load()

  if (!autoplay) {
    try {
      await waitForAudioReady(audio, generation, generationRef)
    } catch (error) {
      logControllerDebug('audio-ready-rejected', audio, {
        autoplay,
        error: formatPlaybackError(error),
        generation,
        reason,
        src: nextSrc,
        trackKey: `${track.type}|${track.creator}|${track.title}`,
      })
      throw error
    }

    if (generationRef && generationRef.current !== generation) {
      logControllerDebug('audio-load-cancelled', audio, {
        autoplay,
        generation,
        reason,
        src: nextSrc,
        trackKey: `${track.type}|${track.creator}|${track.title}`,
      })
      return { ok: false, cancelled: true }
    }

    return { ok: true, autoplay: false, src: nextSrc }
  }

  // autoplay === true: call play() immediately after load() to preserve the
  // iOS Media Session activation window — waiting for canplay loses it.
  if (generationRef && generationRef.current !== generation) {
    logControllerDebug('audio-load-cancelled', audio, {
      autoplay,
      generation,
      reason,
      src: nextSrc,
      trackKey: `${track.type}|${track.creator}|${track.title}`,
    })
    return { ok: false, cancelled: true }
  }

  const played = await requestAudioPlay(audio, {
    reason,
    trackKey: `${track.type}|${track.creator}|${track.title}`,
    src: nextSrc,
  })

  return { ok: played, autoplay: true, src: nextSrc }
}

function safeSetActionHandler(action, handler) {
  try {
    navigator.mediaSession.setActionHandler(action, handler)
  } catch (error) {
    console.warn(`Media Session action "${action}" is not supported`, error)
  }
}

export function updateMediaSessionMetadata(track, API_URL) {
  if (!('mediaSession' in navigator)) return

  if (!track) {
    console.log('[DEBUG:metadata-null]', new Error().stack)
    navigator.mediaSession.metadata = null
    return
  }

  const coverUrl = buildMediaUrl(track, API_URL, 'cover')
  const artwork = coverUrl
    ? [
        { src: coverUrl, sizes: '96x96', type: 'image/jpeg' },
        { src: coverUrl, sizes: '256x256', type: 'image/jpeg' },
        { src: coverUrl, sizes: '512x512', type: 'image/jpeg' },
      ]
    : []

  navigator.mediaSession.metadata = new MediaMetadata({
    title: track.title,
    artist: track.creator,
    artwork,
  })
}

export function setMediaSessionHandlers({
  onPlay,
  onPause,
  onNext,
  onPrevious,
}) {
  if (!('mediaSession' in navigator)) {
    return () => {}
  }

  safeSetActionHandler('play', onPlay ?? null)
  safeSetActionHandler('pause', onPause ?? null)
  safeSetActionHandler('nexttrack', onNext ?? null)
  safeSetActionHandler('previoustrack', onPrevious ?? null)

  return () => {
    safeSetActionHandler('play', null)
    safeSetActionHandler('pause', null)
    safeSetActionHandler('nexttrack', null)
    safeSetActionHandler('previoustrack', null)
  }
}

export function updateMediaSessionPlaybackState(state) {
  if (!('mediaSession' in navigator)) return

  try {
    navigator.mediaSession.playbackState = state
  } catch (error) {
    console.warn('Unable to update Media Session playbackState', error)
  }
}

export function updateMediaSessionPosition(audio) {
  if (!('mediaSession' in navigator)) return
  if (typeof navigator.mediaSession.setPositionState !== 'function') return
  if (!audio) return

  const duration = Number(audio.duration)
  if (!Number.isFinite(duration) || duration <= 0) return

  try {
    navigator.mediaSession.setPositionState({
      duration,
      playbackRate: audio.playbackRate || 1,
      position: Math.min(audio.currentTime || 0, duration),
    })
  } catch (error) {
    console.warn('Unable to update Media Session position state', error)
  }
}

export function clearMediaSession() {
  // console.log('[DEBUG:clearMediaSession]', new Error().stack)
  if (!('mediaSession' in navigator)) return

  updateMediaSessionMetadata(null)
  updateMediaSessionPlaybackState('none')

  safeSetActionHandler('play', null)
  safeSetActionHandler('pause', null)
  safeSetActionHandler('nexttrack', null)
  safeSetActionHandler('previoustrack', null)
}
